import React, { useState } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { useMutation } from '@apollo/client';
import Toast from 'react-native-toast-message';
import { useSearchParams } from '../../react-router';
import Constants from '../../constants';
import { Access, UserContext } from '../../contexts/user';
import {
  formatSubscriptionDate,
  normalizeSubscriptionPlan,
  useSubscription,
} from '../../contexts/subscription';
import {
  CREATE_BILLING_PORTAL_SESSION,
  CREATE_CHECKOUT_SESSION,
  CREATE_STAFF_SEAT_CHECKOUT_SESSION,
} from '../../graphql/subscription';
import {
  PAYWALL_PLANS,
  PaywallPlan,
  PlanId,
  SELF_SERVE_PLAN_IDS,
  formatPlanPrice,
  planById,
} from '../../utils/paywall';
import { EXTRA_STAFF_SEAT_MONTHLY_USD } from '../../utils/staffSeats';

const purchasablePlans = PAYWALL_PLANS.filter((plan) => SELF_SERVE_PLAN_IDS.includes(plan.id));

function canPurchase(access?: Access): boolean {
  return access === Access.OWNER || access === Access.ADMIN;
}

function mutationError(payload?: { errors?: string[] }, fallback?: string): string {
  if (payload?.errors && payload.errors.length > 0) {
    return payload.errors[0];
  }
  return fallback || 'Something went wrong. Try again.';
}

export default function PlanCheckout({ required }: { required?: boolean }): JSX.Element {
  const { state } = React.useContext(UserContext);
  const subscription = useSubscription();
  const planOnUser = normalizeSubscriptionPlan(state.user?.organization?.subscriptionPlan);
  const currentPlan = subscription.status === 'ready' ? subscription.planId : planOnUser;
  const [searchParams] = useSearchParams();
  const checkoutReturn = searchParams.get('checkout');
  const [pendingCheckout, setPendingCheckout] = useState<PlanId | 'seats' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [createCheckout, createCheckoutData] = useMutation(CREATE_CHECKOUT_SESSION, {
    fetchPolicy: 'no-cache',
  });
  const [createSeatCheckout, createSeatCheckoutData] = useMutation(CREATE_STAFF_SEAT_CHECKOUT_SESSION, {
    fetchPolicy: 'no-cache',
  });
  const [createPortal, createPortalData] = useMutation(CREATE_BILLING_PORTAL_SESSION, {
    fetchPolicy: 'no-cache',
  });

  const allowed = canPurchase(state.user?.organization.access);
  const busy = createCheckoutData.loading
    || createSeatCheckoutData.loading
    || createPortalData.loading;
  const endsOn = formatSubscriptionDate(subscription.currentPeriodEnd);
  const trialEndsOn = formatSubscriptionDate(subscription.trialEndsAt);

  const cardOnFile = subscription.card || state.user?.organization?.billing?.card || null;

  const startCheckout = async (target: PlanId | 'seats') => {
    if (busy) {
      return;
    }
    setPendingCheckout(target);
    setErrorMessage(undefined);
    const origin = window.location.origin;
    const successUrl = `${origin}/plans?checkout=success`;
    const cancelUrl = `${origin}/plans?checkout=canceled`;
    try {
      const result = target === 'seats'
        ? await createSeatCheckout({ variables: { quantity: 1, successUrl, cancelUrl } })
        : await createCheckout({ variables: { planId: target, successUrl, cancelUrl } });
      const payload = target === 'seats'
        ? result.data?.createStaffSeatCheckoutSession
        : result.data?.createCheckoutSession;
      if (!payload?.success || !payload.url) {
        const message = mutationError(payload, 'Could not open Stripe checkout.');
        setErrorMessage(message);
        Toast.show({ type: 'error', text1: message });
        return;
      }
      window.location.assign(payload.url);
    } catch (error) {
      const graphqlMessage = error && typeof error === 'object' && 'graphQLErrors' in error
        ? (error as { graphQLErrors?: { message?: string }[] }).graphQLErrors
          ?.map((item) => item.message)
          .filter(Boolean)
          .join(' ')
        : '';
      const raw = graphqlMessage || (error instanceof Error ? error.message : '');
      const message = raw && !/status code|Network error/i.test(raw)
        ? raw
        : 'Could not open Stripe checkout.';
      setErrorMessage(message);
      Toast.show({ type: 'error', text1: message });
    } finally {
      setPendingCheckout(null);
    }
  };

  const startPortal = async () => {
    if (busy) {
      return;
    }
    setErrorMessage(undefined);
    const returnUrl = `${window.location.origin}/plans`;
    try {
      const result = await createPortal({ variables: { returnUrl } });
      const payload = result.data?.createBillingPortalSession;
      if (!payload?.success || !payload.url) {
        const message = mutationError(payload, 'Could not open the billing portal.');
        setErrorMessage(message);
        Toast.show({ type: 'error', text1: message });
        return;
      }
      window.location.assign(payload.url);
    } catch (error) {
      const graphqlMessage = error && typeof error === 'object' && 'graphQLErrors' in error
        ? (error as { graphQLErrors?: { message?: string }[] }).graphQLErrors
          ?.map((item) => item.message)
          .filter(Boolean)
          .join(' ')
        : '';
      const raw = graphqlMessage || (error instanceof Error ? error.message : '');
      const message = raw && !/status code|Network error/i.test(raw)
        ? raw
        : 'Could not open the billing portal.';
      setErrorMessage(message);
      Toast.show({ type: 'error', text1: message });
    }
  };

  if (!state.user) {
    return (
      <View style={styles.page}>
        <Text style={styles.heading}>Plans</Text>
      </View>
    );
  }

  if (currentPlan) {
    const canManage = allowed && (currentPlan === 'practice' || currentPlan === 'clinic');
    return (
      <View style={styles.page}>
        <Text style={styles.heading}>Plans</Text>
        <CurrentPlan planId={currentPlan} cardLast4={cardOnFile?.last4}>
          {canManage ? (
            <Pressable
              disabled={busy}
              onPress={startPortal}
              style={[styles.manageButton, busy && styles.selectButtonDisabled]}
            >
              <Text style={styles.manageButtonText}>
                {createPortalData.loading ? 'Opening billing portal...' : 'Manage subscription'}
              </Text>
            </Pressable>
          ) : null}
        </CurrentPlan>
        {subscription.cancelAtPeriodEnd ? (
          <View style={styles.notice}>
            <Text style={styles.noticeText}>
              {planById(currentPlan).name} cancels {endsOn ? `on ${endsOn}` : 'at the end of this billing period'}.
              The organization keeps access until then.
            </Text>
          </View>
        ) : null}
        {errorMessage ? <Text style={styles.error}>{errorMessage}</Text> : null}
      </View>
    );
  }

  if (!allowed) {
    return (
      <View style={styles.page}>
        <Text style={styles.heading}>{required ? 'Choose a plan to continue' : 'Plans'}</Text>
        <Text style={styles.subheading}>
          {subscription.trialActive
            ? `This organization is on a free trial${trialEndsOn ? ` until ${trialEndsOn}` : ''}. An owner or admin can choose Practice or Clinic before it ends.`
            : 'An owner or admin needs to choose Practice or Clinic before this organization can continue.'}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.page}>
      <Text style={styles.heading}>{required ? 'Choose a plan to continue' : 'Plans'}</Text>
      {subscription.trialActive ? (
        <View style={[styles.notice, { marginTop: 16 }]}>
          <Text style={styles.noticeText}>
            {trialEndsOn
              ? `Free trial ends on ${trialEndsOn}. Practice or Clinic keeps access after that.`
              : 'This organization is on a free trial. Practice or Clinic keeps access after it ends.'}
          </Text>
        </View>
      ) : null}
      <Text style={styles.subheading}>
        Practice and Clinic include the full platform. They differ by monthly price and how many
        practitioners and staff are included. Each additional team member is $49/mo.
      </Text>

      <View style={styles.cards}>
        {purchasablePlans.map((plan) => (
          <PlanChoice
            key={plan.id}
            plan={plan}
            current={false}
            loading={pendingCheckout === plan.id}
            disabled={busy}
            onCheckout={() => startCheckout(plan.id)}
          />
        ))}
        <StaffSeatTile
          loading={pendingCheckout === 'seats'}
          disabled={busy}
          onCheckout={() => startCheckout('seats')}
        />
      </View>

      {checkoutReturn === 'success' ? (
        <View style={styles.notice}>
          <Text style={styles.noticeText}>
            Stripe checkout finished. This page updates when the subscription is confirmed.
          </Text>
        </View>
      ) : null}
      {checkoutReturn === 'canceled' ? (
        <View style={styles.notice}>
          <Text style={styles.noticeText}>Checkout was canceled. No charge was made.</Text>
        </View>
      ) : null}

      {errorMessage ? <Text style={styles.error}>{errorMessage}</Text> : null}
    </View>
  );
}

function StaffSeatTile({
  loading,
  disabled,
  onCheckout,
}: {
  loading: boolean;
  disabled: boolean;
  onCheckout: () => void;
}): JSX.Element {
  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <Text style={styles.cardName}>Staff seat</Text>
        <View style={styles.badgePlaceholder} />
      </View>
      <View style={styles.priceRow}>
        <Text style={styles.price}>${EXTRA_STAFF_SEAT_MONTHLY_USD}</Text>
        <Text style={styles.perMonth}>/mo</Text>
      </View>
      <Text style={styles.audience}>One extra login, billed on its own subscription</Text>
      <Text style={styles.seats}>A separate subscription. Each person gets their own login.</Text>
      <Pressable
        disabled={disabled}
        onPress={onCheckout}
        style={[styles.selectButton, styles.selectButtonOn, disabled && styles.selectButtonDisabled]}
      >
        <Text style={[styles.selectButtonText, styles.selectButtonTextOn]}>
          {loading ? 'Opening checkout...' : 'Checkout'}
        </Text>
      </Pressable>
    </View>
  );
}

function CurrentPlan({
  planId,
  cardLast4,
  children,
}: {
  planId: PlanId;
  cardLast4?: string;
  children?: React.ReactNode;
}): JSX.Element {
  const plan = planById(planId);
  return (
    <View style={styles.currentPlan}>
      <Text style={styles.currentPlanLabel}>You are currently subscribed to</Text>
      <Text style={styles.currentPlanName}>{plan.name}</Text>
      <Text style={styles.currentPlanDetail}>
        {plan.monthlyPrice != null ? `${formatPlanPrice(plan.monthlyPrice)}/mo. ` : ''}
        {plan.seats}
        {cardLast4 ? `. Card ending in ${cardLast4}` : ''}
      </Text>
      {children}
    </View>
  );
}

function PlanChoice({
  plan,
  current,
  loading,
  disabled,
  onCheckout,
}: {
  plan: PaywallPlan;
  current: boolean;
  loading: boolean;
  disabled: boolean;
  onCheckout: () => void;
}): JSX.Element {
  return (
    <View style={[styles.card, plan.highlighted && styles.cardHighlight]}>
      <View style={styles.cardHeader}>
        <Text style={styles.cardName}>{plan.name}</Text>
        {current ? (
          <Text style={styles.badge}>Current plan</Text>
        ) : plan.highlighted ? (
          <Text style={styles.badge}>Most clinics</Text>
        ) : (
          <View style={styles.badgePlaceholder} />
        )}
      </View>
      <View style={styles.priceRow}>
        <Text style={styles.price}>{formatPlanPrice(plan.monthlyPrice)}</Text>
        <Text style={styles.perMonth}>/mo</Text>
      </View>
      <Text style={styles.audience}>{plan.audience}</Text>
      <Text style={styles.seats}>{plan.seats}</Text>
      <Pressable
        disabled={disabled || current}
        onPress={onCheckout}
        style={[styles.selectButton, !current && styles.selectButtonOn, (disabled || current) && styles.selectButtonDisabled]}
      >
        <Text style={[styles.selectButtonText, !current && styles.selectButtonTextOn]}>
          {loading ? 'Opening checkout...' : current ? 'Current plan' : 'Checkout'}
        </Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    padding: 28,
    maxWidth: 920,
  },
  heading: {
    fontFamily: 'GothamBold',
    fontSize: 28,
    color: Constants.colors.dark,
  },
  currentPlan: {
    backgroundColor: Constants.colors.lightBlue,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    marginTop: 16,
    marginBottom: 22,
  },
  currentPlanLabel: {
    fontFamily: 'GothamMedium',
    fontSize: 15,
    color: Constants.colors.dark,
  },
  currentPlanName: {
    fontFamily: 'GothamBold',
    fontSize: 22,
    color: Constants.colors.darkBlue,
    marginTop: 4,
  },
  currentPlanDetail: {
    fontFamily: 'Gotham',
    fontSize: 14,
    color: Constants.colors.dark,
    marginTop: 4,
  },
  subheading: {
    fontFamily: 'Gotham',
    fontSize: 15,
    color: Constants.colors.secondary,
    marginTop: 6,
    marginBottom: 22,
  },
  notice: {
    backgroundColor: Constants.colors.lightYellow,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginBottom: 18,
  },
  noticeText: {
    fontFamily: 'Gotham',
    fontSize: 14,
    color: Constants.colors.dark,
  },
  cards: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 22,
  },
  card: {
    flex: 1,
    flexBasis: 280,
    minWidth: 260,
    backgroundColor: Constants.colors.white,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Constants.colors.secondaryLight,
    padding: 18,
  },
  cardHighlight: {
    borderColor: Constants.colors.mediumBlue,
  },
  cardSelected: {
    borderColor: Constants.colors.darkBlue,
    backgroundColor: Constants.colors.lightBlue,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  cardName: {
    fontFamily: 'GothamBold',
    fontSize: 18,
    color: Constants.colors.darkBlue,
  },
  badge: {
    fontFamily: 'GothamMedium',
    fontSize: 11,
    color: Constants.colors.darkBlue,
    backgroundColor: Constants.colors.white,
    borderRadius: 20,
    overflow: 'hidden',
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  badgePlaceholder: {
    minWidth: 72,
    height: 22,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 10,
  },
  price: {
    fontFamily: 'GothamBold',
    fontSize: 32,
    color: Constants.colors.dark,
  },
  perMonth: {
    fontFamily: 'Gotham',
    fontSize: 14,
    color: Constants.colors.secondary,
    marginLeft: 4,
    marginBottom: 5,
  },
  audience: {
    fontFamily: 'GothamMedium',
    fontSize: 14,
    color: Constants.colors.dark,
    marginBottom: 8,
  },
  seats: {
    fontFamily: 'GothamMedium',
    fontSize: 13,
    color: Constants.colors.darkBlue,
    marginBottom: 16,
  },
  selectButton: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Constants.colors.darkBlue,
    paddingVertical: 10,
    alignItems: 'center',
  },
  selectButtonOn: {
    backgroundColor: Constants.colors.darkBlue,
  },
  selectButtonText: {
    fontFamily: 'GothamMedium',
    fontSize: 14,
    color: Constants.colors.darkBlue,
  },
  selectButtonTextOn: {
    color: Constants.colors.white,
  },
  selectButtonDisabled: {
    opacity: 0.6,
  },
  checkout: {
    backgroundColor: Constants.colors.white,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Constants.colors.secondaryLight,
    padding: 18,
    maxWidth: 520,
  },
  checkoutTitle: {
    fontFamily: 'GothamBold',
    fontSize: 16,
    color: Constants.colors.dark,
    marginBottom: 4,
  },
  checkoutDetail: {
    fontFamily: 'Gotham',
    fontSize: 14,
    color: Constants.colors.secondary,
    marginBottom: 12,
  },
  link: {
    fontFamily: 'Gotham',
    fontSize: 13,
    color: Constants.colors.blue,
    textDecorationLine: 'underline',
    marginTop: 12,
  },
  error: {
    fontFamily: 'Gotham',
    fontSize: 14,
    color: Constants.colors.red,
    marginTop: 12,
  },
  manageButton: {
    alignSelf: 'flex-start',
    marginTop: 16,
    backgroundColor: Constants.colors.darkBlue,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 18,
    minWidth: 220,
    alignItems: 'center',
  },
  manageButtonText: {
    fontFamily: 'GothamMedium',
    fontSize: 14,
    color: Constants.colors.white,
  },
});
